import React, { memo, useEffect, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { MDBRow, MDBCol } from "mdbreact";

import { Label, Input, AbsCheckBox } from "abs-ui-components";

import {
	INPUT_STEP_VALUE,
	preventScientificNotation,
	roundOff,
} from "../../../services/utilities";
import MainEngineFormGroup from "./MainEngineFormGroup";
import AuxEngineFormGroup from "./AuxEngineFormGroup";
import DWTGTChangeDetails from "./DWTGTChangeDetails";

const VesselParticularsFormGroup = (props) => {
	const {
		control,
		formState: { errors },
		setValue,
		trigger,
		setError,
		clearErrors,
	} = useFormContext();

	const [hasGrossTonnageChanged, setHasGrossTonnageChanged] = useState(false);
	const [hasDeadWeightChanged, setHasDeadWeightChanged] = useState(false);
	const vesselCapacityChangeDetails = props?.vesselData?.VesselCapacityChangeDtls;
	//console.log("VesselParticularsFormGroup - vesselCapacityChangeDetails:", vesselCapacityChangeDetails);
	// Extract specific items from vesselCapacityDetails array
	const dwtItem = vesselCapacityChangeDetails?.find(x => (x.ChangeType || x.changeType) === 2);
	const gtItem  = vesselCapacityChangeDetails?.find(x => (x.ChangeType || x.changeType) === 1
);

	//console.log("VesselParticularsFormGroup - vesselCapacityChangeDetails:", dwtItem, gtItem);
	//console.log("VesselParticularsFormGroup - dwtItem:", dwtItem);
	//console.log("VesselParticularsFormGroup - gtItem:", gtItem);
 

	const handleGrossTonnageCheckboxChange = () => {
		setHasGrossTonnageChanged((prev) => {
			const checked = !prev;
			setValue(
				"vesselCapacityChangeRequests.0.hasChanged",
				checked
			);
			if (!checked) {
				setValue(
					"vesselCapacityChangeRequests.0",
					{}
				);
				 clearErrors("vesselCapacityChangeRequests.0");
			}
			return checked;
		});
	};
	const hasGrossData = vesselCapacityChangeDetails?.some(
		item => item.ChangeType === 1 && item.HasChanged
	);

	const handleDeadWeightCheckboxChange = () => {
		setHasDeadWeightChanged((prev) => {
			const checked = !prev;
			setValue(
				"vesselCapacityChangeRequests.1.hasChanged",
				checked
			);
			if (!checked) {
				setValue(
					"vesselCapacityChangeRequests.1",
					{}
				);
				clearErrors("vesselCapacityChangeRequests.1");
			}
			return checked;
		});
	};
	const hasDeadweightData = vesselCapacityChangeDetails?.some(
		item => item.ChangeType === 2 && item.HasChanged
	);

	useEffect(() => {
		if (!vesselCapacityChangeDetails?.length) return;
		const grossTonnageData = vesselCapacityChangeDetails.find(
			item => item.ChangeType === 1
		);
		const deadWeightData = vesselCapacityChangeDetails.find(
			item => item.ChangeType === 2
		);
		setHasGrossTonnageChanged(grossTonnageData?.HasChanged ?? false);
		setHasDeadWeightChanged(deadWeightData?.HasChanged ?? false);
	}, [vesselCapacityChangeDetails]);


	const handleBlur = async (field, value) => {
		if (!isNaN(value)) {
			if (+value > 0) {
				setValue(field, roundOff(value));
			}
		} else {
			return null;
		}
		await trigger(field);
	};

	const handleYODBlur = async (field, value) => {
		await trigger(field);
	};

	useEffect(() => {
		if (props?.sumofMainEnginePowers !== undefined) {
			const mainEnginePower = props?.sumofMainEnginePowers
				? roundOff(props?.sumofMainEnginePowers)
				: "";
			setValue(
				`vesselAttributes.totalRatedMainPropulsionPowerOutput`,
				mainEnginePower
			);

			// Validate main engine power
			if (
				props?.regulations?.trim() !== "EUMRV" &&
				props?.regulations?.trim() !== "UKMRV" &&
				props?.regulations?.trim() !== "DCS"
			) {
				if (mainEnginePower === "") {
					setError(
						"vesselAttributes.totalRatedMainPropulsionPowerOutput",
						{
							type: "manual",
							message: "Please enter main propulsion power.",
						}
					);
				} else if (mainEnginePower <= 0) {
					setError(
						"vesselAttributes.totalRatedMainPropulsionPowerOutput",
						{
							type: "manual",
							message: "Please enter a value greater than zero.",
						}
					);
				} else if (mainEnginePower > 200000) {
					setError(
						"vesselAttributes.totalRatedMainPropulsionPowerOutput",
						{
							type: "manual",
							message:
								"Please enter a value less than or equal to 200,000.",
						}
					);
				} else {
					clearErrors(
						"vesselAttributes.totalRatedMainPropulsionPowerOutput"
					);
				}
			}
		}

		if (props?.sumofAuxEnginePowers !== undefined) {
			const auxEnginePower = props?.sumofAuxEnginePowers
				? roundOff(props?.sumofAuxEnginePowers)
				: "";
			setValue(
				`vesselAttributes.totalRatedAuxiliaryEnginePowerOutput`,
				auxEnginePower
			);

			// Validate auxiliary engine power
			if (
				props?.regulations?.trim() !== "EUMRV" &&
				props?.regulations?.trim() !== "UKMRV" &&
				props?.regulations?.trim() !== "DCS"
			) {
				if (auxEnginePower === "") {
					setError(
						"vesselAttributes.totalRatedAuxiliaryEnginePowerOutput",
						{
							type: "manual",
							message: "Please enter auxiliary engine power.",
						}
					);
				} else if (auxEnginePower <= 0) {
					setError(
						"vesselAttributes.totalRatedAuxiliaryEnginePowerOutput",
						{
							type: "manual",
							message: "Please enter a value greater than zero.",
						}
					);
				} else if (auxEnginePower > 200000) {
					setError(
						"vesselAttributes.totalRatedAuxiliaryEnginePowerOutput",
						{
							type: "manual",
							message:
								"Please enter a value less than or equal to 200,000.",
						}
					);
				} else {
					clearErrors(
						"vesselAttributes.totalRatedAuxiliaryEnginePowerOutput"
					);
				}
			}
		}
	}, [
		props.imoBasedData,
		setValue,
		props.sumofAuxEnginePowers,
		props.sumofMainEnginePowers,
		setError,
		clearErrors,
		props.regulations,
	]);

	return (
		<div className="container-fluid p-0 m-0">
			<MDBRow>
				<MDBCol md="12" className="mt-3">
					<Label
						required
						bold
						name="Gross Tonnage"
						className="mb-1"
					/>
					<Controller
						name="vesselAttributes.grossTonnage"
						control={control}
						rules={
							props?.regulations?.trim() !== "EUMRV" &&
								props?.regulations?.trim() !== "UKMRV" &&
								props?.regulations?.trim() !== "DCS"
								? {
									required:
										"The Gross Tonnage field is required",
									min: {
										value: Number.EPSILON,
										message:
											"Please enter a value greater than zero.",
									},
								}
								: {}
						}
						defaultValue={
							props.vesselMstId
								? roundOff(props.vesselData?.GrossTonnage)
								: ""
						}
						render={({ field }) => (
							<>
								<Input
									name={field.name}
									id="vessel_gross_tonnage"
									autoFocus={false}
									value={field.value}
									onChange={field.onChange}
									type="number"
									className="input-height"
									onBlur={(e) =>
										handleBlur(field.name, field.value)
									}
									onKeyDown={(e) => {
										preventScientificNotation(e);
										// Prevent up and down arrow keys
										if (
											e.key === "ArrowUp" ||
											e.key === "ArrowDown"
										) {
											e.preventDefault();
										}
									}}
									onWheel={(e) => e.target.blur()} //prevent mouse scroll bar action
									onInput={(e) => {
										if (e.target.value.length > 7) {
											e.target.value =
												e.target.value.slice(0, 7);
										}
									}}
									//condition for field disable in child, parent report & edit vessel
									disabled={
										!props.isEnrollVessel &&
										props.vesselMstId &&
										(props?.regulations?.trim() ===
											"EUMRV" ||
											"UKMRV" ||
											"DCS")
									}
									step={INPUT_STEP_VALUE}
								/>
							</>
						)}
					/>
					{errors.vesselAttributes &&
						errors.vesselAttributes.grossTonnage && (
							<div className="error-msg">
								{errors.vesselAttributes.grossTonnage.message}
							</div>
						)}
					<div>
						{
							<div style={{ marginLeft: "-18px" }}>
								<AbsCheckBox
									slim={true}
									id="grossTonnageChanged"
									checked={hasGrossTonnageChanged}
									disabled={!props.isEnrollVessel}
									handleChange={() => {
										if (!props.isEnrollVessel) return;
										handleGrossTonnageCheckboxChange();
									}}
									label={<span className="sm">Vessel has Changed Gross Tonnage</span>}
								/>
							</div>
						}
						
						{hasGrossTonnageChanged
						
						 && (
							
							<DWTGTChangeDetails
								index={0}
								isGrossTonnage={true}
								vesselCapacityChangeDetails={vesselCapacityChangeDetails}
								isEnrollVessel={!props.isEnrollVessel}
    							showWarning={gtItem?.ShowWarning==true}
    							reportYear={gtItem?.ReportYear}
								
								
							/>
						)}
					</div>
				</MDBCol>

				<MDBCol md="12" className="mt-3">
					<Label required bold name="Net Tonnage" className="mb-1" />
					<Controller
						name="vesselAttributes.netTonnage"
						control={control}
						defaultValue={
							props?.vesselMstId
								? roundOff(props.vesselData?.NetTonnage)
								: ""
						}
						rules={
							props?.regulations?.trim() !== "EUMRV" &&
								props?.regulations?.trim() !== "UKMRV" &&
								props?.regulations?.trim() !== "DCS"
								? {
									required:
										"The Net Tonnage field is required.",
									min: {
										value: Number.EPSILON,
										message:
											"Please enter a value greater than zero.",
									},
								}
								: {}
						}
						render={({ field }) => (
							<>
								<Input
									name={field.name}
									id="vessel_net_tonnage"
									autoFocus={false}
									value={field.value}
									onChange={field.onChange}
									className="input-height"
									type="number"
									step={INPUT_STEP_VALUE}
									onBlur={(e) =>
										handleBlur(field.name, field.value)
									}
									onKeyDown={(e) => {
										preventScientificNotation(e);
										// Prevent up and down arrow keys
										if (
											e.key === "ArrowUp" ||
											e.key === "ArrowDown"
										) {
											e.preventDefault();
										}
									}}
									onWheel={(e) => e.target.blur()} //prevent mouse scroll bar action
									onInput={(e) => {
										if (e.target.value.length > 7) {
											e.target.value =
												e.target.value.slice(0, 7);
										}
									}}
									//condition for field disable in child, parent report & edit vessel
									disabled={
										!props.isEnrollVessel &&
										props.vesselMstId &&
										(props?.regulations?.trim() ===
											"EUMRV" ||
											"UKMRV" ||
											"DCS")
									}
								/>
							</>
						)}
					/>
					{errors.vesselAttributes &&
						errors.vesselAttributes.netTonnage && (
							<div className="error-msg">
								{errors.vesselAttributes.netTonnage.message}
							</div>
						)}
				</MDBCol>

				<MDBCol md="12" className="mt-3">
					<Label
						required
						bold
						name="Deadweight (MT)"
						className="mb-1"
					/>
					<Controller
						name="vesselAttributes.deadweight"
						control={control}
						defaultValue={
							props.vesselMstId
								? roundOff(props.vesselData?.DeadWeight)
								: ""
						}
						rules={
							props?.regulations?.trim() !== "EUMRV" &&
								props?.regulations?.trim() !== "UKMRV" &&
								props?.regulations?.trim() !== "DCS"
								? {
									required: "Please enter a deadweight",
									min: {
										value: Number.EPSILON,
										message:
											"Please enter a value greater than zero.",
									},
								}
								: {}
						}
						render={({ field }) => (
							<Input
								name={field.name}
								id="vessel_dead_weight"
								autoFocus={false}
								value={field.value}
								onChange={field.onChange}
								type="number"
								className="input-height"
								step={INPUT_STEP_VALUE}
								onBlur={(e) =>
									handleBlur(field.name, field.value)
								}
								onKeyDown={(e) => {
									preventScientificNotation(e);
									// Prevent up and down arrow keys
									if (
										e.key === "ArrowUp" ||
										e.key === "ArrowDown"
									) {
										e.preventDefault();
									}
								}}
								onWheel={(e) => e.target.blur()} //prevent mouse scroll bar action
								onInput={(e) => {
									if (e.target.value.length > 7) {
										e.target.value = e.target.value.slice(
											0,
											7
										);
									}
								}}
								//condition for field disable in child, parent report & edit vessel
								disabled={
									!props.isEnrollVessel &&
									props.vesselMstId &&
									(props?.regulations?.trim() === "EUMRV" ||
										"UKMRV" ||
										"DCS")
								}
							/>
						)}
					/>
					{errors.vesselAttributes &&
						errors.vesselAttributes.deadweight && (
							<div className="error-msg">
								{errors.vesselAttributes.deadweight.message}
							</div>
						)}
					<div>
							<div style={{ marginLeft: "-18px" }}>
								<AbsCheckBox
									slim={true}
									id="deadWeightChanged"
									checked={hasDeadWeightChanged}
									handleChange={() => {
										if (!props.isEnrollVessel) return;
										handleDeadWeightCheckboxChange();
									}}
									label={<span className="sm">Vessel has Changed Deadweight</span>}
								/>

						</div>
						{hasDeadWeightChanged &&
							<DWTGTChangeDetails
								index={1}
								isGrossTonnage={false}
								vesselCapacityChangeDetails={vesselCapacityChangeDetails}
								isEnrollVessel={!props.isEnrollVessel}
    							showWarning={dwtItem?.ShowWarning==true}
    							reportYear={dwtItem?.ReportYear}
								
								
							/>
						}

					</div>
				</MDBCol>

				<MDBCol md="12" className="mt-3">
					<Label
						required
						bold
						name="Total Rated Main Propulsion Power Output (kW)"
						className="mb-1"
					/>
					<Controller
						name="vesselAttributes.totalRatedMainPropulsionPowerOutput"
						control={control}
						rules={
							props?.regulations?.trim() !== "EUMRV" &&
								props?.regulations?.trim() !== "UKMRV" &&
								props?.regulations?.trim() !== "DCS"
								? {
									required:
										"Please enter main propulsion power",
									min: {
										value: Number.EPSILON,
										message:
											"Please enter a value greater than zero.",
									},
									max: {
										value: 200000,
										message:
											"Please enter a value less than or equal to 200,000.",
									},
								}
								: {}
						}
						defaultValue={
							props.vesselMstId
								? roundOff(
									props.vesselData?.MainPropulsionPower
								)
								: ""
						}
						render={({ field }) => (
							<>
								<Input
									name={field.name}
									id="vessel_total_main_engine_power"
									autoFocus={false}
									value={field.value}
									onChange={field.onChange}
									type="number"
									step={INPUT_STEP_VALUE}
									className="input-height"
									max="200000"
									onBlur={(e) =>
										handleBlur(field.name, field.value)
									}
									onKeyDown={(e) => {
										preventScientificNotation(e);
										// Prevent up and down arrow keys
										if (
											e.key === "ArrowUp" ||
											e.key === "ArrowDown"
										) {
											e.preventDefault();
										}
									}}
									onWheel={(e) => e.target.blur()} //prevent mouse scroll bar action
									onInput={(e) => {
										if (e.target.value.length > 50) {
											e.target.value =
												e.target.value.slice(0, 50);
										}
									}}
									disabled
									readOnly
								/>
							</>
						)}
					/>
					{errors.vesselAttributes &&
						errors.vesselAttributes
							.totalRatedMainPropulsionPowerOutput && (
							<div className="error-msg">
								{
									errors.vesselAttributes
										.totalRatedMainPropulsionPowerOutput
										.message
								}
							</div>
						)}
				</MDBCol>

				<MDBCol md="12">
					<MainEngineFormGroup {...props} />
				</MDBCol>

				<MDBCol md="12" className="mt-3">
					<Label
						required
						bold
						name="Total Rated Auxiliary Engine(s) Power Output (kW)"
						className="mb-1"
					/>
					<Controller
						name="vesselAttributes.totalRatedAuxiliaryEnginePowerOutput"
						control={control}
						rules={
							props?.regulations?.trim() !== "EUMRV" &&
								props?.regulations?.trim() !== "UKMRV" &&
								props?.regulations?.trim() !== "DCS"
								? {
									required:
										"Please enter aux propulsion power",
									min: {
										value: Number.EPSILON,
										message:
											"Please enter a value greater than zero.",
									},
									max: {
										value: 200000,
										message:
											"Please enter a value less than or equal to 200,000.",
									},
								}
								: {}
						}
						defaultValue={
							props.vesselMstId
								? roundOff(props.vesselData?.AuxEnginePower)
								: ""
						}
						render={({ field }) => (
							<Input
								name={field.name}
								id="vessel_total_aux_engine_power"
								autoFocus={false}
								value={field.value}
								onChange={field.onChange}
								type="number"
								className="input-height"
								max="200000"
								step={INPUT_STEP_VALUE}
								onBlur={(e) =>
									handleBlur(field.name, field.value)
								}
								onKeyDown={(e) => {
									preventScientificNotation(e);
									// Prevent up and down arrow keys
									if (
										e.key === "ArrowUp" ||
										e.key === "ArrowDown"
									) {
										e.preventDefault();
									}
								}}
								onWheel={(e) => e.target.blur()} //prevent mouse scroll bar action
								onInput={(e) => {
									if (e.target.value.length > 50) {
										e.target.value = e.target.value.slice(
											0,
											50
										);
									}
								}}
								disabled
								readOnly
							/>
						)}
					/>
					{errors.vesselAttributes &&
						errors.vesselAttributes
							.totalRatedAuxiliaryEnginePowerOutput && (
							<div className="error-msg">
								{
									errors.vesselAttributes
										.totalRatedAuxiliaryEnginePowerOutput
										.message
								}
							</div>
						)}
				</MDBCol>

				<MDBCol md="12">
					<AuxEngineFormGroup {...props} />
				</MDBCol>

				<MDBCol md="12" className="mt-3">
					<Label
						bold
						name="Year of Delivery"
						className="mb-1"
						required
					/>
					<Controller
						name="vesselAttributes.yearOfDelivery"
						control={control}
						required
						rules={
							props?.regulations?.trim() !== "EUMRV" ||
								props?.regulations?.trim() !== "UKMRV" ||
								props?.regulations?.trim() !== "DCS"
								? {
									required:
										"Please enter year of delivery",
									validate: (value) =>
										value.toString().length === 4 ||
										"Year of Delivery must be exactly 4 characters",
								}
								: {}
						}
						defaultValue={
							props.vesselMstId
								? props.vesselData?.YearOfDelivery === 0
									? ""
									: props.vesselData?.YearOfDelivery
								: ""
						}
						render={({ field }) => (
							<Input
								name={field.name}
								id="vessel_year_of_delivery"
								autoFocus={false}
								value={field.value}
								onChange={field.onChange}
								type="number"
								className="input-height"
								minLength={4}
								maxLength={4}
								onBlur={(e) =>
									handleYODBlur(field.name, field.value)
								}
								onInput={(e) => {
									if (e.target.value.length > 4) {
										e.target.value = e.target.value.slice(
											0,
											4
										);
									}
								}}
								onKeyDown={(e) => {
									preventScientificNotation(e);
									// Prevent up and down arrow keys
									if (
										e.key === "ArrowUp" ||
										e.key === "ArrowDown"
									) {
										e.preventDefault();
									}
								}}
								onWheel={(e) => e.target.blur()} //prevent mouse scroll bar action
								//condition for field disable in child, parent report & edit vessel
								disabled={
									!props.isEnrollVessel &&
									props.vesselMstId &&
									(props?.regulations?.trim() === "EUMRV" ||
										"UKMRV" ||
										"DCS")
								}
							/>
						)}
					/>
					{errors.vesselAttributes &&
						errors.vesselAttributes.yearOfDelivery && (
							<div className="error-msg">
								{errors.vesselAttributes.yearOfDelivery.message}
							</div>
						)}
				</MDBCol>

				<MDBCol md="12">
					<MDBRow>
						<MDBCol md="3" className="mt-3">
							<Label
								bold
								name="LBP (molded) (m)"
								className="mb-1"
							/>
							<Controller
								name="vesselAttributes.lengthBetweenPerpendiculars"
								control={control}
								rules={
									props?.regulations?.trim() !== "EUMRV" &&
										props?.regulations?.trim() !== "UKMRV" &&
										props?.regulations?.trim() !== "DCS"
										? {
											min: {
												value: Number.EPSILON,
												message:
													"Please enter a value greater than zero.",
											},
										}
										: {}
								}
								defaultValue={
									props.vesselMstId
										? props.vesselData
											?.LengthBetweenPerp === 0 ||
											props.vesselData
												?.LengthBetweenPerp === null
											? ""
											: roundOff(
												props.vesselData
													?.LengthBetweenPerp
											)
										: ""
								}
								render={({ field }) => (
									<Input
										name={field.name}
										id="vessel_length_between_perp"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										type="number"
										className="input-height"
										step={INPUT_STEP_VALUE}
										onBlur={(e) =>
											handleBlur(field.name, field.value)
										}
										onKeyDown={(e) => {
											preventScientificNotation(e);
											// Prevent up and down arrow keys
											if (
												e.key === "ArrowUp" ||
												e.key === "ArrowDown"
											) {
												e.preventDefault();
											}
										}}
										onWheel={(e) => e.target.blur()} //prevent mouse scroll bar action
										onInput={(e) => {
											if (e.target.value.length > 7) {
												e.target.value =
													e.target.value.slice(0, 7);
											}
										}}
										//condition for field disable in child, parent report & edit vessel
										disabled={
											!props.isEnrollVessel &&
											props.vesselMstId &&
											(props?.regulations?.trim() ===
												"EUMRV" ||
												"UKMRV" ||
												"DCS")
										}
									/>
								)}
							/>
							{errors.vesselAttributes &&
								errors.vesselAttributes
									.lengthBetweenPerpendiculars && (
									<div className="error-msg">
										{
											errors.vesselAttributes
												.lengthBetweenPerpendiculars
												.message
										}
									</div>
								)}
						</MDBCol>

						<MDBCol md="3" className="mt-3">
							<Label
								bold
								name="Beam (molded) (m)"
								className="mb-1"
							/>
							<Controller
								name="vesselAttributes.beam"
								control={control}
								rules={
									props?.regulations?.trim() !== "EUMRV" &&
										props?.regulations?.trim() !== "UKMRV" &&
										props?.regulations?.trim() !== "DCS"
										? {
											min: {
												value: Number.EPSILON,
												message:
													"Please enter a value greater than zero.",
											},
										}
										: {}
								}
								defaultValue={
									props.vesselMstId
										? props.vesselData?.Beam === 0 ||
											props.vesselData?.Beam === null
											? ""
											: roundOff(props.vesselData?.Beam)
										: ""
								}
								render={({ field }) => (
									<Input
										name={field.name}
										id="vessel_beam"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										type="number"
										className="input-height"
										step={INPUT_STEP_VALUE}
										onBlur={(e) =>
											handleBlur(field.name, field.value)
										}
										onKeyDown={(e) => {
											preventScientificNotation(e);
											// Prevent up and down arrow keys
											if (
												e.key === "ArrowUp" ||
												e.key === "ArrowDown"
											) {
												e.preventDefault();
											}
										}}
										onWheel={(e) => e.target.blur()} //prevent mouse scroll bar action
										onInput={(e) => {
											if (e.target.value.length > 7) {
												e.target.value =
													e.target.value.slice(0, 7);
											}
										}}
										//condition for field disable in child, parent report & edit vessel
										disabled={
											!props.isEnrollVessel &&
											props.vesselMstId &&
											(props?.regulations?.trim() ===
												"EUMRV" ||
												"UKMRV" ||
												"DCS")
										}
									/>
								)}
							/>
							{errors.vesselAttributes &&
								errors.vesselAttributes.beam && (
									<div className="error-msg">
										{errors.vesselAttributes.beam.message}
									</div>
								)}
						</MDBCol>

						<MDBCol md="6" className="mt-3">
							<Label
								bold
								name="Maximum Draught at Summer Load Line (molded) (m)"
								className="mb-1"
							/>
							<Controller
								name="vesselAttributes.maximumDraughtAtSummerLoadLine"
								control={control}
								rules={
									props?.regulations?.trim() !== "EUMRV" &&
										props?.regulations?.trim() !== "UKMRV" &&
										props?.regulations?.trim() !== "DCS"
										? {
											min: {
												value: Number.EPSILON,
												message:
													"Please enter a value greater than zero.",
											},
										}
										: {}
								}
								defaultValue={
									props.vesselMstId
										? props.vesselData
											?.MaxDraughtSummerLoadline ===
											0 ||
											props.vesselData
												?.MaxDraughtSummerLoadline ===
											null
											? ""
											: roundOff(
												props.vesselData
													?.MaxDraughtSummerLoadline
											)
										: ""
								}
								render={({ field }) => (
									<Input
										name={field.name}
										id="vessel_max_draught_summer_load_line"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										type="number"
										className="input-height"
										step={INPUT_STEP_VALUE}
										onBlur={(e) =>
											handleBlur(field.name, field.value)
										}
										onKeyDown={(e) => {
											preventScientificNotation(e);
											// Prevent up and down arrow keys
											if (
												e.key === "ArrowUp" ||
												e.key === "ArrowDown"
											) {
												e.preventDefault();
											}
										}}
										onWheel={(e) => e.target.blur()} //prevent mouse scroll bar action
										onInput={(e) => {
											if (e.target.value.length > 7) {
												e.target.value =
													e.target.value.slice(0, 7);
											}
										}}
										//condition for field disable in child, parent report & edit vessel
										disabled={
											!props.isEnrollVessel &&
											props.vesselMstId &&
											(props?.regulations?.trim() ===
												"EUMRV" ||
												"UKMRV" ||
												"DCS")
										}
									/>
								)}
							/>
							{errors.vesselAttributes &&
								errors.vesselAttributes
									.maximumDraughtAtSummerLoadLine && (
									<div className="error-msg">
										{
											errors.vesselAttributes
												.maximumDraughtAtSummerLoadLine
												.message
										}
									</div>
								)}
						</MDBCol>
					</MDBRow>
				</MDBCol>
			</MDBRow>
		</div>
	);
};

export default memo(VesselParticularsFormGroup);
